import { useCaptchaGeneration } from 'lib/hooks/useCaptchaGeneration';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import clsx from 'clsx';
import useDictionary from 'lib/hooks/useDictionary';
import styles from './Registration.module.scss';
import SuccessGate from 'components/authorable/shared/userprofile/SignUp/SuccessGate';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { DEFAULT_MY_PROFILE_URL } from 'lib/utils/user-profile-urls';
import { getCookie, setCookie } from 'lib/utils/cookies';

interface RegistrationProps {
  title?: string;
  description?: string;
}

interface RegistrationFormData {
  email: string;
  password: string;
  birthDate: string; // Format: MM/YYYY
  zipCode: string;
  subscriptions: {
    subscriptionId: string;
    subscribed: boolean;
  }[];
  termsOfUseAcceptance: string;
}

const UNDERAGE_COOKIE_NAME = 'NotEligible';
const UNDERAGE_COOKIE_DAYS = 30;

type RegistrationScreenState = 'form' | 'success' | 'underage';

const errorMessages: Record<string, string> = {
  EMAIL_ALREADY_EXISTS: 'Components.Register.EmailAlreadyExists',
  REGISTRATION_FAILED: 'Components.Register.RegistrationFailed',
  EMAIL_REQUIRED: 'Components.Register.EmailRequired',
  PASSWORD_REQUIRED: 'Components.Register.PasswordRequired',
  PASSWORD_TOO_SHORT: 'Components.Register.PasswordTooShort',
  BIRTH_DATE_REQUIRED: 'Components.Register.BirthDateRequired',
  INVALID_BIRTH_DATE: 'Components.Register.InvalidBirthDate',
  INVALID_ZIP_CODE: 'Components.Register.InvalidZipCode',
  ZIP_CODE_REQUIRED: 'Components.Register.ZipcodeRequired',
  INVALID_EMAIL: 'Components.Register.InvalidEmail',
  MINIMUM_AGE_REQUIRED: 'Components.Register.MinimumAgeRequired',
  GENERIC_ERROR: 'Components.Register.GenericError',
  CAPTCHA_FAILED: 'Components.Register.CaptchaFailed',
  BANNER_MESSAGE: 'Components.Register.BannerMessage',
  SUMMARY_INVALID_DATE: 'Components.Register.SummaryInvalidDate',
  SUMMARY_INVALID_ZIP: 'Components.Register.SummaryInvalidZip',
  VALIDATION_SUMMARY_PREFIX: 'Components.Register.ValidationSummaryPrefix',
  EMAIL_ALREADY_EXISTS_DETAIL: 'Components.Register.EmailAlreadyExistsDetail',
  // Profile & Briteverify rule validation errors
  EMAIL_FORMAT: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailFormat',
  EMAIL_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailProfanity',
  FIRST_NAME_CHAR: 'Common.LoginAndRegistration.EmailRegistrationResponses.FirstNameChar',
  FIRST_NAME_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.FirstNameProfanity',
  LAST_NAME_CHAR: 'Common.LoginAndRegistration.EmailRegistrationResponses.LastNameChar',
  LAST_NAME_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.LastNameProfanity',
  EMAIL_DISPOSABLE: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailDisposable',
  EMAIL_ACCOUNT_INVALID:
    'Common.LoginAndRegistration.EmailRegistrationResponses.EmailAccountInvalid',
  EMAIL_ADDRESS_INVALID:
    'Common.LoginAndRegistration.EmailRegistrationResponses.EmailAddressInvalid',
  EMAIL_DOMAIN_INVALID: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailDomainInvalid',
  EMAIL_ROLE_INVALID: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailRoleInvalid',
  PROFILE_FAIL: 'Common.LoginAndRegistration.EmailRegistrationResponses.ProfileFail',
} as const;

// Helper function to generate current timestamp in the required format
const generateTermsOfUseAcceptance = (offsetMinutes = -300) => {
  const now = new Date(Date.now() + offsetMinutes * 60000);
  const offset = `${offsetMinutes <= 0 ? '-' : '+'}${String(
    Math.floor(Math.abs(offsetMinutes) / 60)
  ).padStart(2, '0')}:${String(Math.abs(offsetMinutes) % 60).padStart(2, '0')}`;

  return now
    .toISOString()
    .replace('Z', offset)
    .replace(/(\.\d{3})Z?/, '$10000'); // extend milliseconds to 7 digits
};

const VALIDATION_SUMMARY_PREFIX =
  'There was an error signing you up. Please check the below errors and try again.';
const SUMMARY_INVALID_DATE_FALLBACK = 'Please enter a valid date.';
const SUMMARY_INVALID_ZIP_FALLBACK = 'Please enter a valid ZIP code.';

const getValidationSummaryError = (
  errors: Record<string, string>,
  getDictionaryValue: (key: string, fallback?: string) => string
) => {
  const orderedFields: Array<keyof RegistrationFormData> = [
    'email',
    'password',
    'birthDate',
    'zipCode',
  ];

  const prefix = getDictionaryValue(
    errorMessages.VALIDATION_SUMMARY_PREFIX,
    VALIDATION_SUMMARY_PREFIX
  );

  const messages = orderedFields
    .map((field) => {
      const fieldError = errors[field as string];
      if (!fieldError) {
        return null;
      }

      if (
        field === 'birthDate' &&
        fieldError !==
          getDictionaryValue(errorMessages.BIRTH_DATE_REQUIRED, 'Birth date is required')
      ) {
        return getDictionaryValue(
          errorMessages.SUMMARY_INVALID_DATE,
          SUMMARY_INVALID_DATE_FALLBACK
        );
      }

      if (
        field === 'zipCode' &&
        fieldError !== getDictionaryValue(errorMessages.ZIP_CODE_REQUIRED, 'Zip code is required')
      ) {
        return getDictionaryValue(errorMessages.SUMMARY_INVALID_ZIP, SUMMARY_INVALID_ZIP_FALLBACK);
      }

      return fieldError.endsWith('.') ? fieldError : `${fieldError}.`;
    })
    .filter((message): message is string => !!message);

  return `${prefix} ${messages.join(' ')}`.trim();
};

const Registration: React.FC<RegistrationProps> = ({
  title = 'Sign Up',
  description = '* Required',
}) => {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const birthDateInputRef = useRef<HTMLInputElement>(null);
  const zipCodeInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<RegistrationFormData>({
    email: '',
    password: '',
    birthDate: '',
    zipCode: '',
    subscriptions: [{ subscriptionId: '1e34a555-89d1-45f0-8f07-b3ae6b01c03c', subscribed: true }],
    termsOfUseAcceptance: generateTermsOfUseAcceptance(), //'2025-09-26T13:29:07.0507950-05:00', //generateTermsOfUseAcceptance(),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [screenState, setScreenState] = useState<RegistrationScreenState>('form');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Use our optimized captcha hook
  const { captchaToken } = useCaptchaGeneration('user_registration');
  const { getDictionaryValue } = useDictionary();

  useEffect(() => {
    if (getCookie(UNDERAGE_COOKIE_NAME) === 'true') {
      setSubmitError('');
      setFieldErrors({});
      setScreenState('underage');
    }
  }, []);

  const focusFirstInvalidField = useCallback((errors: Record<string, string>) => {
    const firstInvalidField = ['email', 'password', 'birthDate', 'zipCode'].find(
      (field) => !!errors[field]
    );

    const fieldRefMap: Record<string, React.RefObject<HTMLInputElement | null>> = {
      email: emailInputRef,
      password: passwordInputRef,
      birthDate: birthDateInputRef,
      zipCode: zipCodeInputRef,
    };

    const targetRef = firstInvalidField ? fieldRefMap[firstInvalidField] : undefined;
    if (targetRef?.current) {
      // Defer focus so the summary alert renders and announces before field-level feedback.
      setTimeout(() => targetRef.current?.focus(), 0);
    }
  }, []);

  // Update form data
  const updateFormData = useCallback(
    (field: keyof RegistrationFormData, value: string | number) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear field error when user starts typing
      if (fieldErrors[field]) {
        setFieldErrors((prev) => ({ ...prev, [field]: '' }));
      }
      // Clear submit error when user starts typing
      if (submitError) {
        setSubmitError('');
      }
    },
    [fieldErrors, submitError]
  );

  // Update subscription
  const updateSubscription = useCallback((subscriptionId: string, subscribed: boolean) => {
    setFormData((prev) => ({
      ...prev,
      subscriptions: prev.subscriptions.map((sub) =>
        sub.subscriptionId === subscriptionId ? { ...sub, subscribed } : sub
      ),
    }));
  }, []);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!formData.email.trim()) {
      errors.email = getDictionaryValue(errorMessages.EMAIL_REQUIRED) || 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email =
        getDictionaryValue(errorMessages.INVALID_EMAIL) || 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      errors.password =
        getDictionaryValue(errorMessages.PASSWORD_REQUIRED) || 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password =
        getDictionaryValue(errorMessages.PASSWORD_TOO_SHORT) ||
        'Password must be at least 8 characters long';
    }

    // firstName and lastName are not required by the API, so we don't validate them

    if (!formData.birthDate.trim()) {
      errors.birthDate =
        getDictionaryValue(errorMessages.BIRTH_DATE_REQUIRED) || 'Birth date is required';
    } else {
      // Validate MM/YYYY format
      const birthDateRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
      if (!birthDateRegex.test(formData.birthDate)) {
        errors.birthDate =
          getDictionaryValue(errorMessages.INVALID_BIRTH_DATE) ||
          'Please enter birth date in MM/YYYY format (e.g., 06/1984)';
      } else {
        const [month, year] = formData.birthDate.split('/');
        const birthMonth = parseInt(month);
        const birthYear = parseInt(year);
        const currentYear = new Date().getFullYear();

        if (birthMonth < 1 || birthMonth > 12) {
          errors.birthDate =
            getDictionaryValue(errorMessages.INVALID_BIRTH_DATE) ||
            'Please enter a valid month (01-12)';
        } else if (birthYear < 1900 || birthYear > currentYear) {
          errors.birthDate =
            getDictionaryValue(errorMessages.INVALID_BIRTH_DATE) || 'Please enter a valid year';
        }
      }
    }

    if (!formData.zipCode.trim()) {
      errors.zipCode =
        getDictionaryValue(errorMessages.ZIP_CODE_REQUIRED) || 'Zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      errors.zipCode =
        getDictionaryValue(errorMessages.INVALID_ZIP_CODE) ||
        'Please enter a valid zip code (e.g., 12345 or 12345-6789)';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmitError(getValidationSummaryError(errors, getDictionaryValue));
      focusFirstInvalidField(errors);
    } else {
      setSubmitError('');
    }
    return Object.keys(errors).length === 0;
  }, [formData, focusFirstInvalidField, getDictionaryValue]);

  // Reset the form
  const resetForm = useCallback(() => {
    setFormData({
      email: '',
      password: '',
      birthDate: '',
      zipCode: '',
      subscriptions: [{ subscriptionId: '1e34a555-89d1-45f0-8f07-b3ae6b01c03c', subscribed: true }],
      termsOfUseAcceptance: generateTermsOfUseAcceptance(),
    });
    setSubmitError('');
    setFieldErrors({});
  }, []);

  const showUnderageScreen = useCallback(() => {
    setCookie(UNDERAGE_COOKIE_NAME, 'true', UNDERAGE_COOKIE_DAYS);
    setSubmitError('');
    setFieldErrors({});
    setScreenState('underage');
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setScreenState('form');

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      // Get a fresh captcha token
      const token = await captchaToken();
      if (!token) {
        setSubmitError(
          getDictionaryValue(errorMessages.CAPTCHA_FAILED) ||
            'Security verification failed. Please refresh the page and try again.'
        );
        return;
      }

      // Start submission
      setIsSubmitting(true);

      // Parse birth date to extract month and year
      const [birthMonth, birthYear] = formData.birthDate.split('/').map((num) => parseInt(num));

      // Filter subscriptions to only include checked ones
      const checkedSubscriptions = formData.subscriptions.filter((sub) => sub.subscribed);

      // Prepare registration data - only send fields that the API actually uses
      const registrationData: {
        type: 'credentials';
        email: string;
        password: string;
        birthMonth: number;
        birthYear: number;
        zipCode: string;
        termsOfUseAcceptance: string;
        captchaToken: string;
        subscriptions?: {
          subscriptionId: string;
          subscribed: boolean;
        }[];
      } = {
        type: 'credentials',
        email: formData.email,
        password: formData.password,
        birthMonth: birthMonth,
        birthYear: birthYear,
        zipCode: formData.zipCode,
        termsOfUseAcceptance: formData.termsOfUseAcceptance,
        captchaToken: token,
      };

      // Only include subscriptions if at least one is checked
      if (checkedSubscriptions.length > 0) {
        registrationData.subscriptions = checkedSubscriptions;
      }

      // Call the registration API
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const result = await response.json();
      console.log('result registration api call', result);
      console.log('response registration api call', response);
      if (!response.ok) {
        // Handle specific error cases
        if (
          result.error === 'MINIMUM_AGE_REQUIRED' ||
          result.errors?.some(
            (errorObj: { error: string }) => errorObj.error === 'MINIMUM_AGE_REQUIRED'
          )
        ) {
          showUnderageScreen();
        } else if (result.error === 'EMAIL_ALREADY_EXISTS') {
          setFieldErrors({
            email:
              getDictionaryValue(errorMessages.EMAIL_ALREADY_EXISTS) ||
              'This email address is already registered',
          });
          setSubmitError(
            getDictionaryValue(errorMessages.EMAIL_ALREADY_EXISTS_DETAIL) ||
              'This email address is already registered. Please use a different email or try logging in.'
          );
        } else if (result.errors && result.errors.length > 0) {
          // Handle Profile/Briteverify rule-based errors from the errors array
          const fieldErrorAccumulator: Record<string, string> = {};
          const bannerMessages: string[] = [];

          result.errors.forEach((errorObj: { error: string; field: string }) => {
            const errorKey = errorMessages[errorObj.error];
            if (errorKey) {
              const errorMessage = getDictionaryValue(errorKey);
              if (errorMessage) {
                // Accumulate field-level errors (last write wins per field)
                if (errorObj.field) {
                  fieldErrorAccumulator[errorObj.field] = errorMessage;
                }
                bannerMessages.push(errorMessage);
              }
            }
          });

          if (Object.keys(fieldErrorAccumulator).length > 0) {
            setFieldErrors(fieldErrorAccumulator);
          }

          if (bannerMessages.length > 0) {
            setSubmitError(Array.from(new Set(bannerMessages)).join(' '));
          } else {
            setSubmitError(
              getDictionaryValue(errorMessages.PROFILE_FAIL) ||
                getDictionaryValue(errorMessages.REGISTRATION_FAILED) ||
                'Registration failed. Please try again.'
            );
          }
        } else {
          setSubmitError(
            getDictionaryValue(errorMessages.REGISTRATION_FAILED) ||
              'Registration failed. Please try again.'
          );
        }
        return;
      }
      console.log('Setting submitSuccess to true');
      // Handle success
      setScreenState('success');
      resetForm();
    } catch (error) {
      // Handle error
      const errorMessage =
        error instanceof Error
          ? error.message
          : getDictionaryValue(errorMessages.GENERIC_ERROR) ||
            'An error occurred during registration';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={clsx(styles.registrationFormContent)}>
      {screenState !== 'form' ? (
        <SuccessGate
          title={screenState === 'success' ? 'Success!' : "We're sorry!"}
          description={
            screenState === 'success' ? (
              'Lorem ipsum dolor sit amet, consectetur adipiscing elit.'
            ) : (
              <>
                You are not eligible to join the Betty Crocker community.
                <br />
                <br />
                But guess what? You don&apos;t need to be a member to get thousands of great recipes
                and how-to videos.
                <br />
                So stick around and enjoy!
              </>
            )
          }
          button={
            screenState === 'success' ? (
              <CtaLink
                ctaStyle="filled"
                ctaVariant="brand"
                text="My Account"
                url={DEFAULT_MY_PROFILE_URL}
              />
            ) : undefined
          }
          icon={undefined}
          variant={screenState === 'success' ? 'success' : 'underage'}
          testId={
            screenState === 'success'
              ? 'registration-success-screen'
              : 'registration-underage-screen'
          }
        />
      ) : (
        <>
          <div className="form-header">
            <h2>{title}</h2>
          </div>
          <div className="form-description">
            <p>{description}</p>
          </div>

          {submitError && (
            <div className="error-message" role="alert">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className={clsx(styles.registrationForm)} noValidate>
            {/* Personal Information */}
            <fieldset className={clsx(styles.formsection)}>
              <label htmlFor="email">Email Address: *</label>
              <input
                ref={emailInputRef}
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={isSubmitting}
                className={fieldErrors.email ? 'error' : ''}
                aria-describedby={fieldErrors.email ? 'registration-email-error' : undefined}
              />
              {fieldErrors.email && (
                <span id="registration-email-error" className={clsx(styles.errormsg)} role="alert">
                  {fieldErrors.email}
                </span>
              )}
            </fieldset>
            {/* Account Security */}
            <fieldset className={clsx(styles.formsection)}>
              <label htmlFor="password">Password: *</label>
              <input
                ref={passwordInputRef}
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={(e) => updateFormData('password', e.target.value)}
                placeholder="Enter your password (min 8 characters)"
                required
                disabled={isSubmitting}
                className={fieldErrors.password ? 'error' : ''}
                aria-describedby={fieldErrors.password ? 'registration-password-error' : undefined}
              />
              {fieldErrors.password && (
                <span
                  id="registration-password-error"
                  className={clsx(styles.errormsg)}
                  role="alert"
                >
                  {fieldErrors.password}
                </span>
              )}
            </fieldset>

            {/* Birth Information */}
            <fieldset className={clsx(styles.formsection)}>
              <label htmlFor="birthDate">Birthday *</label>
              <input
                ref={birthDateInputRef}
                type="text"
                id="birthDate"
                name="birthDate"
                value={formData.birthDate}
                onChange={(e) => updateFormData('birthDate', e.target.value)}
                placeholder="MM/YYYY (e.g., 06/1984)"
                required
                disabled={isSubmitting}
                className={fieldErrors.birthDate ? 'error' : ''}
                aria-describedby={
                  fieldErrors.birthDate ? 'registration-birthdate-error' : undefined
                }
              />
              {fieldErrors.birthDate && (
                <span
                  id="registration-birthdate-error"
                  className={clsx(styles.errormsg)}
                  role="alert"
                >
                  {fieldErrors.birthDate}
                </span>
              )}
            </fieldset>
            <fieldset className={clsx(styles.formsection)}>
              <label htmlFor="zipCode">Zip Code *</label>
              <input
                ref={zipCodeInputRef}
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={(e) => updateFormData('zipCode', e.target.value)}
                placeholder="Enter your zip code (e.g., 12345)"
                required
                disabled={isSubmitting}
                className={fieldErrors.zipCode ? 'error' : ''}
                aria-describedby={fieldErrors.zipCode ? 'registration-zipcode-error' : undefined}
              />
              {fieldErrors.zipCode && (
                <span
                  id="registration-zipcode-error"
                  className={clsx(styles.errormsg)}
                  role="alert"
                >
                  {fieldErrors.zipCode}
                </span>
              )}
            </fieldset>

            {/* Subscriptions */}
            <fieldset className={clsx(styles.formsection)}>
              <label>Subscribe to our newsletter</label>

              <div className="subscriptions">
                {formData.subscriptions.map((subscription) => (
                  <div key={subscription.subscriptionId} className="subscription-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={subscription.subscribed}
                        onChange={(e) =>
                          updateSubscription(subscription.subscriptionId, e.target.checked)
                        }
                        disabled={isSubmitting}
                      />
                      <span>
                        {subscription.subscriptionId === '1e34a555-89d1-45f0-8f07-b3ae6b01c03c'
                          ? 'Yes, I’d like to receive the newsletter'
                          : 'Subscribe to promotional emails and special offers'}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
            <fieldset className={clsx(styles.formsection)}>
              <div>
                I accept the General Mills Privacy Policy, and agree that my information may be used
                across General Mills family of brands, I also agree to the Community Rules.
              </div>
            </fieldset>

            <div className="form-actions">
              {submitError && (
                <div className="api-error-message" role="alert">
                  <strong>Registration Error:</strong> {submitError}
                </div>
              )}
              <button type="submit" className={clsx(styles.signupButton)} disabled={isSubmitting}>
                Sign Up
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default Registration;
